import React from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { OneAlert } from '@factorialco/f0-react/dist/experimental'

import i18n from 'modules/core/lib/i18n'

import { CompetencyAssignCard } from 'modules/competencies/components/CompetencyAssignCard'
import RoleLevelsSelector from 'modules/competencies/components/RoleLevelsSelector'

interface CompetencyLevel {
  id: string | number
  name: string
}

type RoleLevelUuids = string[]
type RoleLevelUuidsByCompetencyLevelId = Record<CompetencyLevel['id'], RoleLevelUuids>

interface Assignments {
  competency: RoleLevelUuids
  competencyLevels: RoleLevelUuidsByCompetencyLevelId
}

interface Props {
  competencyName: string
  competencyLevels: CompetencyLevel[]
  assignments: Assignments
  excludeUuids?: RoleLevelUuids
  onAssignToCompetency: (uuids: RoleLevelUuids) => void
  onAssignToCompetencyLevel: (
    competencyLevelId: CompetencyLevel['id'],
    uuids: RoleLevelUuids
  ) => void
}

export const RoleLevelAssignationForm: React.FC<Props> = ({
  competencyName,
  competencyLevels,
  assignments,
  excludeUuids = [],
  onAssignToCompetency,
  onAssignToCompetencyLevel,
}) => {
  const hasCompetencyLevels = competencyLevels && competencyLevels.length > 0

  const roleLevelUuidsToExclude = (competencyLevel: CompetencyLevel) => {
    return Object.entries(assignments.competencyLevels).reduce<RoleLevelUuids>(
      (arr, [levelId, uuids]) =>
        levelId !== String(competencyLevel.id) ? [...arr, ...uuids] : arr,
      excludeUuids
    )
  }

  if (!hasCompetencyLevels) {
    return (
      <Box gap="s16" flexDirection="column">
        <Box gap="s8" flexDirection="column" flexNoShrink>
          <Text.Header2 color="grey1000">
            {i18n.t('competencies_v3.levels.assignation_form.title')}
          </Text.Header2>
          <Text.Normal size="300" color="grey1000">
            {i18n.t('competencies_v3.levels.assignation_form.description.without_levels')}
          </Text.Normal>
        </Box>

        <OneAlert
          variant="info"
          title={i18n.t('competencies_v3.levels.assignation_form.no_levels_callout.title')}
          description={i18n.t(
            'competencies_v3.levels.assignation_form.no_levels_callout.description'
          )}
        />

        <CompetencyAssignCard competencyName={competencyName}>
          <RoleLevelsSelector
            selected={assignments.competency}
            exclude={excludeUuids}
            onChange={onAssignToCompetency}
          />
        </CompetencyAssignCard>
      </Box>
    )
  }

  return (
    <Box gap="s16" flexDirection="column">
      <Box gap="s8" flexDirection="column">
        <Text.Header2 color="grey1000">
          {i18n.t('competencies_v3.levels.assignation_form.title')}
        </Text.Header2>
        <Text.Normal size="300" color="grey1000">
          {i18n.t('competencies_v3.levels.assignation_form.description.with_levels')}
        </Text.Normal>
      </Box>

      <Box gap="s16">
        {competencyLevels.map((level, index) => (
          <CompetencyAssignCard
            key={level.id}
            competencyName={competencyName}
            levelName={level.name}
            position={index + 1}
          >
            <RoleLevelsSelector
              selected={assignments.competencyLevels[String(level.id)] ?? []}
              exclude={roleLevelUuidsToExclude(level)}
              onChange={(uuids) => onAssignToCompetencyLevel(level.id, uuids)}
            />
          </CompetencyAssignCard>
        ))}
      </Box>
    </Box>
  )
}
